import React from "react";
import SEO from "../components/SEO";
import ContactForm from "../components/ContactForm";
import { getOptimizedImageUrl } from "../lib/cms";

export default function Kontakt() {
  return (
    <>
      <SEO
        title="Kontakt"
        description="Professionel og sikker træfældning af alle typer træer – fra simple opgaver til risikofyldte fældninger tæt på bygninger. Erfaring og uddannelse sikrer en veludført opgave."
        ogImage="https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/cas-7.png"
      />

      {/* Hero Header */}
      <section className="bg-[#12281D] text-white py-16 sm:py-20 border-b border-[#1E4231] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
              Kontakt mig
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#C0DACB] leading-relaxed">
              Ring direkte eller send en forespørgsel. Jeg står klar til at rådgive og give et uforpligtende tilbud på din opgave.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content og Form Grid */}
      <section className="py-16 sm:py-24 bg-[#FBF9F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
            {/* Left Contact og Profile Column (6 cols, row 1, col 1) */}
            <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0 space-y-8 lg:pr-8">
              {/* Direct Quick Contact Card overlapping under form */}
              <div className="bg-[#152E22] text-white p-7 sm:p-8 rounded-3xl shadow-none border border-[#255039] space-y-6 relative z-0 lg:-mr-8 lg:pr-14">
                <h2 className="text-2xl font-bold text-white tracking-tight border-b border-white/10 pb-4">
                  Caspers Træservice
                </h2>

                <div className="space-y-4 text-sm sm:text-base">
                  <div className="flex items-center gap-4">
                    <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                        Telefon
                      </span>
                      <a
                        href="tel:+4542169230"
                        className="text-xl font-extrabold text-white hover:text-[#D9691E] transition-colors whitespace-nowrap inline-block"
                      >
                        42 16 92 30
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                      <i className="fa-regular fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                        Mail
                      </span>
                      <a
                        href="mailto:info@casperstraeservice.dk"
                        className="text-base font-bold text-white hover:text-[#D9691E] transition-colors whitespace-nowrap inline-block"
                      >
                        info@casperstraeservice.dk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                        Adresse
                      </span>
                      <p className="text-white font-medium">Fole – 6510 Gram</p>
                      <p className="text-xs text-[#8DB59E]">v/Casper Hartung-Struer</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-8 flex items-center justify-center shrink-0 text-xl text-[#D9691E]">
                      <i className="fa-solid fa-id-card" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs text-[#8DB59E] block uppercase tracking-wider font-semibold">
                        CVR-nummer
                      </span>
                      <span className="text-white font-mono font-bold whitespace-nowrap">
                        37232521
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10 flex items-center gap-3 text-xs text-[#9BBBAA]">
                  <i className="fa-solid fa-shield-check text-[#D9691E] text-base" aria-hidden="true" />
                  <span>Fuldt forsikret op til 10 mio. kr. hos Topdanmark</span>
                </div>
              </div>
            </div>

            {/* Right Contact Form Column (7 cols, row 1, col 6 - overlapping left box) */}
            <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10">
              <ContactForm defaultService="Træfældning" />
            </div>
          </div>

          {/* Profile Presentation Box (redesigned like Home.jsx:163:10 with specified asset image) */}
          <div className="mt-16 sm:mt-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-y-8 lg:gap-y-0 relative">
              {/* Image Side: Bredere og højere, placeret bag ved tekstboksen med overlap */}
              <div className="lg:col-span-6 lg:row-start-1 lg:col-start-1 relative z-0">
                <div className="relative rounded-2xl overflow-hidden h-[420px] sm:h-[500px] lg:h-[620px] w-full">
                  <img
                    src={getOptimizedImageUrl(
                      "https://assets.auxodev.dk/assets/e431b544-04c3-4ed0-906f-0354609bd752/480420683-621080534052185-5375292377406314825-n.jpeg",
                      1200,
                      85
                    )}
                    alt="Casper Hartung-Struer • Caspers Træservice"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Content Side: Boks med baggrundsfarve #1e5d31, ingen shadows eller border, overlapper billedet foran */}
              <div className="lg:col-span-7 lg:row-start-1 lg:col-start-6 relative z-10 bg-[#1e5d31] rounded-2xl p-6 sm:p-9 lg:p-12 space-y-6 shadow-none border-0 text-white">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C5A]">
                  <span>Manden bag Caspers Træservice</span>
                </div>

                <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                  Casper Hartung-Struer
                </h2>

                {/* Profile text */}
                <div className="space-y-4 text-base sm:text-lg text-[#D5E5DB] leading-relaxed font-normal">
                  <p>
                    Mit mål er altid at udføre opgaverne sikkert og professionelt. Erfaring og den fornødne uddannelse er derfor din tryghed for et ordenligt stykke arbejde.
                  </p>
                  <p>
                    God kommunikation fra start til slut er essentielt. Det er ligeledes vigtigt for mig, at kunden altid er tilfreds med det arbejde jeg udfører, og at jeg tager hjem fra opgaven med et positivt indtryk.
                  </p>
                  <p>
                    Jeg løser alle typer af opgaver – fra de store komplekse til de mindre simple. Uanset størrelse kan du regne med at opgaven løses omhyggeligt og gennemført.
                  </p>
                  <p>
                    Jeg har gode tætte samarbejdspartnere som jeg arbejder sammen med, da det ofte er nødvendigt, at vi er flere folk på opgaven så arbejdet kan udføres sikkert og effektivt.
                  </p>
                  <p className="font-semibold text-white">
                    Caspers Træservice er fuldt forsikret.
                  </p>
                </div>

                <div className="pt-2 flex flex-wrap gap-4">
                  <a
                    href="tel:42169230"
                    className="inline-flex items-center gap-2 bg-[#D9691E] hover:bg-[#c35c17] text-white px-5 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap shadow-none"
                  >
                    <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                    <span className="whitespace-nowrap">Ring: 42 16 92 30</span>
                  </a>
                  <a
                    href="mailto:info@casperstraeservice.dk"
                    className="inline-flex items-center gap-2 bg-white hover:bg-[#F9F7F3] text-[#14291E] px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-none"
                  >
                    <i className="fa-regular fa-envelope" aria-hidden="true" />
                    <span>Send en mail</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
